/**
 * Turn the Session Idle Timeout text box into the value the API should receive.
 *
 * Extracted as a pure function, and exported, for two reasons that came out of
 * the ai_review on admin-legal-ai!240:
 *
 *  1. THE INLINE VERSION HAD A SILENT-CLEAR BUG. It was
 *     `raw.trim() === '' ? null : Number(raw)`, and `Number('abc')` is `NaN`.
 *     `JSON.stringify({ sessionIdleMinutes: NaN })` emits `null` — the same wire
 *     value as "clear this policy" — so a value that failed to parse would
 *     silently switch the organisation's idle timeout OFF instead of erroring.
 *     A `type="number"` input makes that hard to reach from the keyboard (a
 *     browser reports `''` for invalid input), but "hard to reach from one
 *     entry point" is not the same as "cannot happen", and the failure is
 *     invisible when it does.
 *
 *  2. IT IS THE ONE PART OF THE SAVE PATH WORTH ASSERTING DIRECTLY. The review
 *     was right that pinning this behaviour with a regex over the component's
 *     source is a proxy: deleting the ternary would leave a source-scan test
 *     green. A pure function can be asserted on its actual output, which is what
 *     `sessionIdlePayload.test.ts` now does.
 */

/**
 * `''` (or whitespace) ⇒ `null`, which CLEARS the policy server-side. Sending
 * `undefined` instead would leave the stored value in place, so an admin who
 * deleted the value would watch it reappear on the next load.
 *
 * A parseable number is sent as a number. The backend re-validates the range and
 * refuses out-of-bounds values with a 400, so this deliberately does NOT clamp —
 * clamping here would save a different policy than the one the admin typed.
 *
 * An UNPARSEABLE value is forwarded as the raw string rather than as `null` or
 * `NaN`. That looks odd and is the point: the backend rejects it with a 400 the
 * admin can see, which is strictly better than silently clearing the policy or
 * silently saving nothing. The return type says `string` for exactly this case.
 */
export function sessionIdlePayload(raw: string): number | string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : trimmed;
}
